/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";

import { AgGridProvider, AgGridReact } from "ag-grid-react";
import { AllCommunityModule } from "ag-grid-community";

import AddEditCategoryDrawer from "./AddEditCategoryDrawer";
import ConfirmDeleteAlert from "./ConfirmDeleteAlert";
import axiosClient from "../../../api/axiosClient";

const modules = [AllCommunityModule];

export default function DynamicTable({ columnsData, endpoint }) {
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({});
  const [page, setPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const loadData = async () => {
    try {
      const res = await axiosClient.get(`${endpoint}?page=${page}`);

      setData(res.data.data || []);
      setMeta(res.data.meta || {});
    } catch (error) {
      console.error(error);
      toast({
        title: "Gagal mengambil data",
        status: "error",
        duration: 2500,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    loadData();
  }, [page]);

  const handleDelete = async () => {
    if (!selectedRow?.id) return;

    try {
      await axiosClient.delete(`${endpoint}/${selectedRow.id}`);

      toast({
        title: "Data berhasil dihapus",
        status: "success",
        duration: 2500,
        isClosable: true,
      });

      onClose();
      setSelectedRow(null);
      await loadData();
    } catch (error) {
      console.error(error);

      toast({
        title: "Gagal menghapus data",
        status: "error",
        duration: 2500,
        isClosable: true,
      });
    }
  };

  const columnDefs = useMemo(() => {
    const mappedColumns = columnsData.map((col) => {
      if (col.accessor === "__index") {
        return {
          headerName: col.Header || "No",
          width: 90,
          pinned: "left",
          filter: false,
          sortable: false,
          valueGetter: (params) => {
            const rowIndex = params.node.rowIndex + 1;
            const perPage = meta?.per_page ?? data.length;
            return (page - 1) * perPage + rowIndex;
          },
        };
      }

      return {
        headerName: col.Header,
        field: col.accessor,
        flex: 1,
        minWidth: 170,
        sortable: true,
        filter: true,
        resizable: true,
        valueGetter: (params) => {
          const value = params.data?.[col.accessor];
          return value ?? "-";
        },
        cellRenderer: (params) => {
          if (col.Cell) {
            return col.Cell(params.value, params.data);
          }

          return params.value ?? "-";
        },
      };
    });

    mappedColumns.push({
      headerName: "Actions",
      field: "actions",
      width: 230,
      pinned: "right",
      sortable: false,
      filter: false,
      resizable: false,
      cellRenderer: (params) => {
        const row = params.data;

        return (
          <Flex gap="10px" align="center" h="100%">
            <AddEditCategoryDrawer editData={row} onSuccess={loadData} />

            <Button
              colorScheme="red"
              size="sm"
              onClick={() => {
                setSelectedRow(row);
                onOpen();
              }}
            >
              Delete
            </Button>
          </Flex>
        );
      },
    });

    return mappedColumns;
  }, [columnsData, page, meta, data]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: true,
    }),
    []
  );

  return (
    <Box w="100%">
      <Flex
        mb="20px"
        justify="space-between"
        align={{ base: "start", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap="14px"
      >
        <Box>
          <Text fontSize="24px" fontWeight="800" color="secondaryGray.900">
            Master Data
          </Text>
          <Text fontSize="sm" color="gray.500">
            Kelola data master ResolveIT
          </Text>
        </Box>

        <AddEditCategoryDrawer onSuccess={loadData} />
      </Flex>

      <Box
        className="ag-theme-quartz"
        w="100%"
        h="600px"
        borderRadius="24px"
        overflow="hidden"
        bg="white"
        boxShadow="0 18px 40px rgba(112, 144, 176, 0.12)"
      >
        <AgGridProvider modules={modules}>
          <AgGridReact
            rowData={data}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowHeight={58}
            headerHeight={52}
            animateRows={true}
            suppressCellFocus={true}
            pagination={false}
            domLayout="normal"
          />
        </AgGridProvider>
      </Box>

      <Flex mt="20px" justifyContent="center" alignItems="center" gap="10px">
        <Button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          isDisabled={page === 1}
        >
          Prev
        </Button>

        <Box>
          Page {page} / {meta?.total_pages ?? 1}
        </Box>

        <Button
          onClick={() => setPage((p) => p + 1)}
          isDisabled={meta?.total_pages === page}
        >
          Next
        </Button>
      </Flex>

      <ConfirmDeleteAlert
        isOpen={isOpen}
        onClose={() => {
          setSelectedRow(null);
          onClose();
        }}
        onConfirm={handleDelete}
      />
    </Box>
  );
}